import { useEffect, useRef, useState } from "react";
import { Color, Mesh, Program, Renderer, Triangle } from "ogl";
import "./SpecularButton.css";

const PAD = 20;

const VERTEX_SHADER = `#version 300 es
in vec2 position;

void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform vec2 uCenter;
uniform vec2 uHalfSize;
uniform float uRadius;
uniform float uAngle;
uniform float uPx;
uniform vec3 uLineColor;
uniform vec3 uBaseColor;
uniform float uIntensity;
uniform float uShineSize;
uniform float uShineFade;
uniform float uThickness;
uniform float uBaseWidth;

out vec4 fragColor;

float roundedRectDistance(vec2 point, vec2 bounds, float radius) {
  vec2 offset = abs(point) - bounds + radius;
  return length(max(offset, 0.0)) + min(max(offset.x, offset.y), 0.0) - radius;
}

float gaussianLine(float distance, float sigma) {
  float normalizedDistance = distance / (sigma + 1e-6);
  float curve = mix(1.0, 1.6, smoothstep(0.0, 1.5, normalizedDistance));
  return exp(-curve * normalizedDistance * normalizedDistance);
}

void main() {
  vec2 point = gl_FragCoord.xy - uCenter;
  float distance = roundedRectDistance(point, uHalfSize, uRadius);
  vec2 lightDirection = vec2(cos(uAngle), sin(uAngle));

  float base = (1.0 - smoothstep(0.0, uBaseWidth, abs(distance))) * 0.45;
  vec2 normal = normalize(point / (uHalfSize * uHalfSize) + 1e-6);
  float angle = acos(clamp(abs(dot(normal, lightDirection)), 0.0, 1.0));
  float rim = 1.0 - smoothstep(
    uShineSize - uShineFade,
    uShineSize + uShineFade + 1e-4,
    angle
  );
  float line = gaussianLine(distance, uThickness);
  float edgeClamp = 1.0 - smoothstep(0.5 * uPx, 3.0 * uPx, abs(distance));
  float highlight = line * rim * edgeClamp * uIntensity;

  vec3 color = uBaseColor * base + uLineColor * highlight;
  float alpha = clamp(base + highlight, 0.0, 1.0);
  fragColor = vec4(color, alpha);
}
`;

function useReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event) => setReducedMotion(event.matches);
    mediaQuery.addEventListener?.("change", handleChange);
    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, []);

  return reducedMotion;
}

export default function SpecularButton({
  children = "Buy Now",
  radius = 16,
  textColor = "#fff0c2",
  lineColor = "#fff0c2",
  baseColor = "#8b6420",
  intensity = 0.78,
  shineSize = 9,
  shineFade = 34,
  thickness = 1.2,
  speed = 0.2,
  proximity = 180,
  className = "",
  onClick,
  type = "button",
  disabled = false,
  ariaBusy = false,
}) {
  const buttonRef = useRef(null);
  const effectRef = useRef(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const button = buttonRef.current;
    const effect = effectRef.current;
    if (!button || !effect || reducedMotion) return undefined;

    let renderer;
    let resizeObserver;
    let animationFrame = 0;
    let destroyed = false;

    try {
      renderer = new Renderer({
        alpha: true,
        premultipliedAlpha: true,
        antialias: true,
        dpr: Math.min(window.devicePixelRatio || 1, 2),
        webgl: 2,
      });
    } catch {
      return undefined;
    }

    const { gl } = renderer;
    if (!(gl instanceof WebGL2RenderingContext)) {
      gl.getExtension("WEBGL_lose_context")?.loseContext();
      return undefined;
    }

    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const geometry = new Triangle(gl);
    if (geometry.attributes.uv) delete geometry.attributes.uv;

    const program = new Program(gl, {
      vertex: VERTEX_SHADER,
      fragment: FRAGMENT_SHADER,
      uniforms: {
        uCenter: { value: [0, 0] },
        uHalfSize: { value: [1, 1] },
        uRadius: { value: 0 },
        uAngle: { value: 2.4 },
        uPx: { value: renderer.dpr },
        uLineColor: { value: [1, 1, 1] },
        uBaseColor: { value: [0.54, 0.39, 0.13] },
        uIntensity: { value: 0 },
        uShineSize: { value: (shineSize * Math.PI) / 180 },
        uShineFade: { value: (shineFade * Math.PI) / 180 },
        uThickness: { value: thickness * renderer.dpr },
        uBaseWidth: { value: renderer.dpr },
      },
    });
    const mesh = new Mesh(gl, { geometry, program });
    const size = { width: 1, height: 1 };
    const highlightColor = new Color(lineColor);
    const edgeColor = new Color(baseColor);

    effect.appendChild(gl.canvas);

    const resize = () => {
      const bounds = button.getBoundingClientRect();
      size.width = bounds.width;
      size.height = bounds.height;
      renderer.setSize(bounds.width + PAD * 2, bounds.height + PAD * 2);
      program.uniforms.uCenter.value = [
        (PAD + bounds.width / 2) * renderer.dpr,
        (PAD + bounds.height / 2) * renderer.dpr,
      ];
      program.uniforms.uHalfSize.value = [
        (bounds.width / 2) * renderer.dpr,
        (bounds.height / 2) * renderer.dpr,
      ];
      program.uniforms.uRadius.value =
        Math.min(radius, Math.min(bounds.width, bounds.height) / 2) *
        renderer.dpr;
    };

    resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(button);
    resize();

    let pointerAngle = null;
    let proximityStrength = 0;
    let currentAngle = 2.4;
    let idleAngle = 2.4;
    let brightness = 0;
    let lastTime = performance.now();

    const handlePointerMove = (event) => {
      const bounds = button.getBoundingClientRect();
      const centerX = bounds.left + bounds.width / 2;
      const centerY = bounds.top + bounds.height / 2;
      const distanceX = Math.max(
        bounds.left - event.clientX,
        0,
        event.clientX - bounds.right,
      );
      const distanceY = Math.max(
        bounds.top - event.clientY,
        0,
        event.clientY - bounds.bottom,
      );
      const distance = Math.hypot(distanceX, distanceY);

      if (distance === 0) {
        const normalizedX =
          (event.clientX - centerX) / (bounds.width / 2);
        const normalizedY =
          (centerY - event.clientY) / (bounds.height / 2);
        pointerAngle =
          Math.atan2(2 / bounds.height, -2 / bounds.width) +
          normalizedX * 0.3 +
          normalizedY * 0.15;
      } else {
        pointerAngle = Math.atan2(
          centerY - event.clientY,
          event.clientX - centerX,
        );
      }

      const strength = Math.max(0, 1 - distance / proximity);
      proximityStrength = strength * strength * (3 - 2 * strength);
    };

    const render = (now) => {
      if (destroyed || document.visibilityState === "hidden") return;

      const delta = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;
      idleAngle += speed * delta;
      const targetAngle = pointerAngle ?? idleAngle;
      const difference =
        ((targetAngle - currentAngle + Math.PI * 3) % (Math.PI * 2)) -
        Math.PI;
      currentAngle += difference * (1 - Math.exp(-delta * 7));
      brightness +=
        (proximityStrength - brightness) * (1 - Math.exp(-delta * 8));

      program.uniforms.uAngle.value = currentAngle;
      program.uniforms.uLineColor.value = [
        highlightColor.r,
        highlightColor.g,
        highlightColor.b,
      ];
      program.uniforms.uBaseColor.value = [
        edgeColor.r,
        edgeColor.g,
        edgeColor.b,
      ];
      program.uniforms.uIntensity.value = intensity * brightness;
      renderer.render({ scene: mesh });
      animationFrame = requestAnimationFrame(render);
    };

    const handleVisibilityChange = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      if (document.visibilityState !== "hidden" && !destroyed) {
        lastTime = performance.now();
        animationFrame = requestAnimationFrame(render);
      }
    };

    window.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    animationFrame = requestAnimationFrame(render);

    return () => {
      destroyed = true;
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
      gl.canvas.remove();
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [
    baseColor,
    intensity,
    lineColor,
    proximity,
    radius,
    reducedMotion,
    shineFade,
    shineSize,
    speed,
    thickness,
  ]);

  return (
    <button
      ref={buttonRef}
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-busy={ariaBusy}
      className={`specular-button${className ? ` ${className}` : ""}`}
      style={{
        "--specular-radius": `${radius}px`,
        "--specular-text-color": textColor,
      }}
    >
      <span
        ref={effectRef}
        className="specular-button__effect"
        aria-hidden="true"
      />
      <span className="specular-button__label">{children}</span>
    </button>
  );
}
